import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import * as Cookie from 'js-cookie';
import config from '@/config';
import store from '@/store';
import { SSOBehavior } from '@/services/merchantSpace/types';
import * as common from '@/services/common/common';
import { RCUIDUserNotRegister } from '@/api/utils';
import { getRouter } from '@/libs/singleton';
import { PlatformKey } from '@/constants';
import Storage, { FORCE_SIGNIN_REDIRECT } from '@/libs/storage';
import { decryptEmailBeforeAssign, isLoginResponse, RegisterDTO, signInCodeFlow } from '../../api/resources/ssoApi';
import { AuthTokens, FederationResponse, LoginResponse, SynchroResponse, ReconcileResponse } from '../userSpace/types';
import { autoApplySSOSynchro, disconnectFromSSO, refreshUser, refreshUserPoints } from '../userSpace/UserService';
import { generatePKCE, getLastCodeVerifier } from './pkce-auth-flow.service';
// TODO: make this less store dependant

const getPlatform = () => store.getters['platform/getPlatform'];
const isPkceAuthEnabled = () => store.getters['platform/isPkceAuthEnabled'];
const getSsoBehavior = () => store.getters['platform/ssoBehavior'];
const getExternalSigninURL = () => store.getters['platform/getExternalSignInURL'];
const getPrompt = () => store.getters['platform/ssoPrompt'];
const isConnected = () => common.isConnected();
const flashDanger = (...args) => store.dispatch('notifications/flashDanger', ...args);
const getClientId = () => config.ssoPartnerClientId;
const getConnectionPath = () => {
  return config.ssoPartnerPath ? config.ssoPartnerPath : 'auth';
};

function getRedirectUri() {
  const platform = getPlatform();
  const { platformUrl, landingPath } = platform.config;

  return `${platformUrl}${landingPath}`;
}

export async function executeSSOFlow() {
  const ssoBehavior = getSsoBehavior();
  const { code, state, error } = getLoginResponse();

  if (error) {
    return;
  }

  if (ssoBehavior === SSOBehavior.AUTO_SIGNIN_TO_EXTERNAL && !code) {
    await startCodeFlow();
  } else if (code) {
    if (!state) {
      throw new Error('SSO: sign in without state params is forbidden');
    }

    // si un code est présent, on connecte l'utilisateur
    // ou on lui demande de s'inscrire (signer les cgus, etc...)
    await completeCodeFlow({ code, state });
  }
}

export const startCodeFlow = doAuthorizeCodeRequest;

async function doAuthorizeCodeRequest({ prompt = getPrompt() }: { prompt?: 'login' | 'none' | 'auto' } = {}) {
  const ssoBehavior = getSsoBehavior();
  const externalSigninURL = getExternalSigninURL();
  const connectionPath = getConnectionPath();

  if (![SSOBehavior.SIGNIN_TO_EXTERNAL, SSOBehavior.AUTO_SIGNIN_TO_EXTERNAL, SSOBehavior.WITH_CLASSIC].includes(ssoBehavior)) {
    return;
  }

  const promptQuery = prompt === 'auto' ? 'approval_prompt' : 'prompt';
  const params = await createParamsForCodeRequest();
  window.location.href = `${externalSigninURL}/${connectionPath}?${params}&${promptQuery}=${prompt}`;
}

async function completeCodeFlow({ code, state }: { code: string; state: string }) {
  if (isConnected()) {
    return;
  }
  const platform = getPlatform();
  const codeVerifier = isPkceAuthEnabled() ? getLastCodeVerifier() : undefined;
  let res;
  try {
    res = await signInCodeFlow({
      platformUUID: platform.uuid,
      state,
      code,
      ...(codeVerifier && { codeVerifier }),
      redirectUri: getRedirectUri(),
      clientId: getClientId(),
    });
  } catch (err) {
    if (err instanceof RCUIDUserNotRegister) {
      window.location.href = platform.config.sso.externalSignUpURL;
      return;
    }

    flashDanger({
      message: 'Une erreur est survenue lors de la connexion. Merci de réessayer ultérieurement',
    });


    throw err;
  }

  // check if we have sso refresh token used to disconnect user
  const ssoRefreshToken = ((res as LoginResponse).tokens && (res as LoginResponse).tokens.ssoRefreshToken)
  || (res as RegisterDTO).metas && (res as RegisterDTO).metas.ssoRefreshToken;

  if(ssoRefreshToken) {
    Cookie.set('SSORefreshToken', ssoRefreshToken, { expires: 31, sameSite: 'strict' });
  }

  const ssoBehavior = store.getters['platform/ssoBehavior'];
  const encryptedSynchroString = localStorage.getItem('synchroString');
  if (ssoBehavior === SSOBehavior.WITH_CLASSIC && encryptedSynchroString) {
    associateSSOAccount(encryptedSynchroString, platform.uuid, res);
    return;
  }

  if ((res as SynchroResponse).synchroURL) {
    store.dispatch('logoutMessageVisible', false);
    store.dispatch('showAskSSOSynchroMessage', true);
    store.dispatch('user/setUserSynchroURL', (res as SynchroResponse).synchroURL);
    return;
  }

  if ((res as FederationResponse).needToFederate) {
    const externalLogoutURL = store.getters['platform/getPlatform'].config.sso.externalLogoutURL;
    if(externalLogoutURL) {
      disconnectFromSSO(externalLogoutURL);
    }
    store.dispatch('showNeedToFederateMessage', true);
    return;
  }

  if ((res as ReconcileResponse).needToReconcile) {
    store.dispatch('user/setReconcileData', {
      userInfos: (res as ReconcileResponse).userInfos,
      platformUUID: (res as ReconcileResponse).platformUUID,
    });
    store.dispatch('showNeedToReconcileMessage', true);
    return;
  }


  // user exists and api answer with an access token
  if (isLoginResponse(res)) {
    await loginAndRedirectToLanding(res.tokens);
  } else {
    // L'utilisateur n'existe pas. Il doit s'inscrire ou relier son "ancien" compte existant
    const router = getRouter();
    // Si d'autres plateformes viennent à utiliser cette feature, préférez gérer le cas à l'aide d'une option d'admin.
    // (et la table platform_config_sso)
    const routeName = platform.key === PlatformKey.BKLUB ? 'sso:pairing_mode_selection' : 'sso:pairing';
    router.push({
      name: routeName,
      query: {
        userInfos: res.userInfos,
        externalAccountId: res.metas.externalAccountId,
        externalAccessToken: res.metas.externalAccessToken,
        relatedTicketings: res.metas.relatedTicketings,
      },
    });
  }
}

// old name: loginAndRedirectUser
export async function loginAndRedirectToLanding(tokens: AuthTokens) {
  // check if we have sso refresh token used to disconnect user
  if(tokens.ssoRefreshToken) {
    Cookie.set('SSORefreshToken', tokens.ssoRefreshToken, { expires: 31, sameSite: 'strict' });
  }

  store.dispatch('user/login', { tokens });
  await Promise.all([refreshUser(), refreshUserPoints()]);
  redirectToLanding();
}

function redirectToLanding() {
  const router = getRouter();
  const platform = getPlatform();

  const forceRedirect = Storage.pull(FORCE_SIGNIN_REDIRECT);
  const pageToShow = platform.config.landingPath;
  if (forceRedirect || router.currentRoute.path !== pageToShow) {
    router.replace(forceRedirect || pageToShow);
  }
}

async function createParamsForCodeRequest(): Promise<URLSearchParams> {
  const state = generateStateParams();

  return new URLSearchParams({
    client_id: getClientId(),
    redirect_uri: getRedirectUri(),
    state,
    response_type: 'code',
    scope: ['profile', 'email', 'openid'].join(' '),
    // for public clients
    ...(isPkceAuthEnabled() && (await generatePKCE())),
  });
}

async function associateSSOAccount(encrypted: string, platformUUID: string, ssoResponse: any) {
  const router = getRouter();
  const decrypted = await decryptEmailBeforeAssign({ encrypted, platformUUID });
  const now = dayjs();
  if (decrypted.date && now.diff(dayjs(decrypted.date), 'minute', true) <= 5) {
    // same emails, we have a synchroURL
    if ((ssoResponse as SynchroResponse).synchroURL) {
      localStorage.removeItem('synchroString');
      window.location.href = (ssoResponse as SynchroResponse).synchroURL;
    } else {
      // le sso flow a reconnu que l'utilisateur sso existait déjà
      if (ssoResponse.tokens) {
        store.dispatch('autoSynchroError', true);
        store.dispatch('showConfirmSSOSynchroMessage', true);
        return;
      }

      // otherwise we force synchro and erase old email with the sso one
      try {
        const synchro = await autoApplySSOSynchro(ssoResponse.userInfos, platformUUID, encrypted);
        if (!synchro.tokens) {
          router.replace('/oops');
          return;
        }
        localStorage.removeItem('synchroString');
        store.dispatch('autoSynchroError', false);
        store.dispatch('showConfirmSSOSynchroMessage', true);
        await loginAndRedirectToLanding(synchro.tokens);
        return;
      } catch (error) {
        console.log('error', error);
        setTimeout(() => router.push('/'), 5000);
      }
    }
  } else {
    router.replace('/oops');
  }
}

function getLoginResponse() {
  const params = new URLSearchParams(window.location.search);
  return {
    code: params.get('code'),
    state: params.get('state'),
    error: params.get('error'),
  };
}

function generateStateParams() {
  return uuidv4();
}
